import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn, UrlTree } from "@angular/router";
import { Observable, firstValueFrom } from "rxjs";
import { OAuthService } from "angular-oauth2-oidc";
import { GenesysCloudService } from "../genesys-cloud.service";
import { Location } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class GenesysLoginService {
  private loginPopup: Window | null = null;
  private silentRefreshConfigured = false;

  constructor(private oAuthService: OAuthService,
    private genesysCloudService: GenesysCloudService, private location: Location) {
  }

  async canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return this.checkGenesysAuthStatus(this.location.path());
  }

  async checkGenesysAuthStatus(url: string): Promise<boolean> {
    if (!this.oAuthService.hasValidAccessToken()) {
      this.handleNoValidAccessToken(url);
      return false;
    }
    return this.initializeGenesysCloudService(url);
  }

  private async initializeGenesysCloudService(url: string): Promise<boolean> {
    try {
      const userDetails = await this.getUserDetails();
      if (!userDetails || !userDetails.id) {
        console.error('User details are missing or incomplete');
        return false;
      }
      localStorage.setItem('genesysUserId', userDetails.id);
      this.genesysCloudService.userDetails.next(userDetails);
      return this.genesysCloudService.initializeWebSocketConnection();
    } catch (error) {
      console.error('Initialization failed', error);
      this.handleNoValidAccessToken(url);      
      return false;
    }
  }

  private async getUserDetails() {
    const accessToken = this.oAuthService.getAccessToken();
    return firstValueFrom(this.genesysCloudService.initialize(accessToken));
  }

  // Make this method public so it can be called from the interceptor
  public handleNoValidAccessToken(url: string) {
    // Genesys uses OAuth implicit flow (oidc: false), not an OpenID discovery endpoint.
    if (this.loginPopup && !this.loginPopup.closed) return;
    if (!this.silentRefreshConfigured) {
      this.oAuthService.setupAutomaticSilentRefresh();
      this.silentRefreshConfigured = true;
    }
    const popLoginUrl = this.buildPopLoginUrl(url);
    this.openLoginPopup(popLoginUrl, url);
  }

  private buildPopLoginUrl(url: string): string {
    return `${this.oAuthService.loginUrl}?response_type=token&client_id=${this.oAuthService.clientId}&redirect_uri=${this.oAuthService.redirectUri}&state=${url}`;
  }

  private openLoginPopup(popLoginUrl: string, url: string) {
    const popup = window.open(popLoginUrl, 'authPopup', 'width=600,height=600');
    if (!popup) return;
    this.loginPopup = popup;
    const interval = setInterval(() => {
      if (popup.closed) {
        clearInterval(interval);
        this.loginPopup = null;
        return;
      }
      if (this.isAccessTokenInPopupUrl(popup)) {
        clearInterval(interval);
        popup.close();
        this.loginPopup = null;
        this.oAuthService.initImplicitFlow(url);
      }
    }, 1000);
  }

  private isAccessTokenInPopupUrl(popup: Window | null): boolean {
    try {
      return !!popup && popup.location.href.includes('access_token=');
    } catch (error) {
      return false;
    }
  }
}

export const GenesysGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {
  return inject(GenesysLoginService).canActivate(next, state);
}
